import { FormikErrors, FormikTouched, useFormik } from 'formik'
import React from 'react'
import * as Yup from 'yup'
import { Checkbox } from '../../../../../components/Checkbox/Checkbox'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { AmountOfObjects, ImportItemType } from '../hooks/useHandleImport'
import translations from '../../../../../translations/en.json'
import { RadioInput } from '../../../../../components/RadioInput/RadioInput'

interface ImportTypeOption {
  id: ImportItemType
  label: string | React.ReactNode
  disabled?: boolean
}

const invalidFeedback = (
  key: keyof IValue,
  touched: FormikTouched<IValue>,
  errors: FormikErrors<IValue>,
) => (touched[key] && !!errors[key] ? errors[key] : null)

interface ITypeEnabledOptions {
  type: ImportItemType
  types?: ImportItemType[]
  amount: AmountOfObjects
}
const isTypeEnabled = ({ type, types = [], amount }: ITypeEnabledOptions) =>
  amount === 'one' ? !!types?.includes(type) && types.length <= 1 : !!types?.includes(type)

type IValue = Record<ImportItemType, boolean>

interface IProps
  extends Omit<
    React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>,
    'onSubmit'
  > {
  amount: AmountOfObjects
  types?: ImportItemType[]
  onBack: (values: ImportItemType[]) => Promise<void> | void
  onSubmit: (values: ImportItemType[]) => Promise<void> | void
}

export const ChooseItemTypeStep = ({
  amount,
  types,
  onSubmit,
  onBack: handleBack,
  ...props
}: IProps) => {
  const {
    errors,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    setFieldError,
    setFieldTouched,
    setValues,
    touched,
    values,
    isValid,
  } = useFormik<IValue>({
    initialValues: {
      contacts: isTypeEnabled({ type: 'contacts', types, amount }),
      attendees: isTypeEnabled({ type: 'attendees', types, amount }),
      registrations: isTypeEnabled({ type: 'registrations', types, amount }),
      events: isTypeEnabled({ type: 'events', types, amount }),
      notes: isTypeEnabled({ type: 'notes', types, amount }),
    },
    validationSchema: Yup.object().shape({
      contacts: Yup.boolean().test(
        'required',
        (translations as any)['error.import_type_required'],
        function () {
          return (
            this.parent.contacts ||
            this.parent.attendees ||
            this.parent.registrations ||
            this.parent.events ||
            this.parent.notes
          )
        },
      ),
      attendees: Yup.boolean().test(
        'required',
        (translations as any)['error.import_type_required'],
        function () {
          return (
            this.parent.contacts ||
            this.parent.attendees ||
            this.parent.registrations ||
            this.parent.events ||
            this.parent.notes
          )
        },
      ),
      events: Yup.boolean().test(
        'required',
        (translations as any)['error.import_type_required'],
        function () {
          return (
            this.parent.contacts ||
            this.parent.attendees ||
            this.parent.registrations ||
            this.parent.events ||
            this.parent.notes
          )
        },
      ),
      registrations: Yup.boolean()
        .test('required', (translations as any)['error.import_type_required'], function () {
          return (
            this.parent.contacts ||
            this.parent.attendees ||
            this.parent.registrations ||
            this.parent.events ||
            this.parent.notes
          )
        })
        .test(
          'amount_requirements',
          (translations as any)['error.import_type_missing_amount_requirements'],
          function (value) {
            return !value || amount === 'many'
          },
        )
        .test(
          'type_requirements',
          (translations as any)['error.import_type_missing_registration_requirements'],
          function (value) {
            return !value || this.parent.attendees
          },
        ),
      notes: Yup.boolean()
        .test('required', (translations as any)['error.import_type_required'], function () {
          return (
            this.parent.contacts ||
            this.parent.attendees ||
            this.parent.registrations ||
            this.parent.events ||
            this.parent.notes
          )
        })
        .test(
          'amount_requirements',
          (translations as any)['error.import_type_missing_amount_requirements'],
          function (value) {
            return !value || amount === 'many'
          },
        )
        .test(
          'requirements',
          (translations as any)['error.import_type_missing_note_requirements'],
          function (value) {
            return !value || this.parent.contacts || this.parent.attendees
          },
        ),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit(
          Object.entries(value)
            .filter((entry): entry is [ImportItemType, boolean] => entry[1])
            .map<ImportItemType>(([key]) => key),
        )
      } catch (e: any) {
        if (e.param) setFieldError(e.param, e.message)
      }
      setSubmitting(false)
    },
  })

  const valueTypes: ImportItemType[] = Object.entries(values)
    .filter((entry): entry is [ImportItemType, boolean] => entry[1])
    .map<ImportItemType>(([key]) => key)

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const { name, checked, value } = e.target
    if (amount === 'one' && value) {
      setValues({
        contacts: value === 'contacts',
        attendees: value === 'attendees',
        registrations: value === 'registrations',
        events: value === 'events',
        notes: value === 'notes',
      })
    }
    if (amount === 'many') {
      setFieldValue(name, checked)
    }
  }

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    const { name, value } = e.target
    if (amount === 'one') {
      setFieldTouched(value, true)
    }
    if (amount === 'many') {
      setFieldTouched(name, true)
    }
  }

  return (
    <form className="py-3" onSubmit={handleSubmit} {...props}>
      <h5 className="mb-4">Select an object you'd like to import</h5>
      <div className="mb-3">
        {new Array<ImportTypeOption>(
          {
            id: 'attendees',
            label: (
              <>
                <div>
                  <b>Attendee</b>
                </div>
                <div className="small">
                  The children or individuals you work with that register for your events.
                </div>
              </>
            ),
          },
          {
            id: 'contacts',
            label: (
              <>
                <div>
                  <b>Contacts</b>
                </div>
                <div className="small">
                  The people that make the purchase, commonly known as parents or customers.
                </div>
              </>
            ),
          },
          {
            id: 'events',
            label: (
              <>
                <div>
                  <b>Events</b>
                  <span className="badge rounded-pill text-bg-light border ms-2">Coming soon</span>
                </div>
                <div className="small">
                  Details on the camps or sessions that you offer to your attendees.
                </div>
              </>
            ),
            disabled: true,
          },
          {
            id: 'registrations',
            label: (
              <>
                <div>
                  <b>Registrations</b>
                  <span className="badge rounded-pill text-bg-light border ms-2">Coming soon</span>
                </div>
                <div className="small">
                  Information that you've collected about your attendees while participating in your
                  program.
                </div>
              </>
            ),
            disabled: true,
          },
          {
            id: 'notes',
            label: (
              <>
                <div>
                  <b>Notes</b>
                  <span className="badge rounded-pill text-bg-light border ms-2">Coming soon</span>
                </div>
                <div className="small">
                  Any extra info you wrote down about a contact or attendee.
                </div>
              </>
            ),
            disabled: true,
          },
        ).map(({ id, label, disabled }) => {
          if (amount === 'one') {
            return (
              <RadioInput
                key={id}
                id={id}
                name="item_type"
                isInvalid={touched[id] && !!errors[id]}
                value={id}
                checked={values[id]}
                disabled={disabled}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                {label}
              </RadioInput>
            )
          }
          return (
            <Checkbox
              key={id}
              id={id}
              name={id}
              inputSize="sm"
              isInvalid={touched[id] && !!errors[id]}
              value={id}
              checked={values[id]}
              disabled={disabled}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {label}
            </Checkbox>
          )
        })}
        <div className="invalid-feedback d-block">
          {invalidFeedback('contacts', touched, errors) ||
            invalidFeedback('attendees', touched, errors) ||
            invalidFeedback('registrations', touched, errors) ||
            invalidFeedback('events', touched, errors) ||
            invalidFeedback('notes', touched, errors) ||
            '\u00A0'}
        </div>
      </div>
      <div className="mb-3 d-flex justify-content-between mt-4">
        <button
          type="button"
          className="btn btn-link btn-block d-md-inline-block w-md-auto mt-2 px-0"
          onClick={() => {
            if (isValid) {
              handleBack(valueTypes)
              return
            }
            handleBack(types || [])
          }}
        >
          Back
        </button>
        <LoadingButton
          type="submit"
          className="btn btn-primary btn-block d-md-inline-block w-md-auto"
          loading={isSubmitting}
        >
          Continue
        </LoadingButton>
      </div>
    </form>
  )
}
